/**
 * ModuleStripCard — surfaces the school/club/union-spec modules as small
 * functional widgets across dashboards: Streak, Effort Leaderboard preview,
 * Squad Feed feed, Match Sheets summary.
 *
 * Used at the top of Player + Coach + School dashboards.
 */
import { useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Flame, Trophy, MessageSquare, ClipboardList, Loader2, Users } from 'lucide-react';
import api from '@/utils/api';

function Tile({ icon: Icon, title, value, sub, testId, accent = '#A3FF00' }) {
  return (
    <Card className="bg-zinc-900 border-zinc-800 p-4" data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-zinc-400 text-xs uppercase tracking-wider">{title}</p>
        <Icon className="w-4 h-4" style={{ color: accent }} />
      </div>
      <p className="text-2xl font-heading font-bold" style={{ color: accent }}>{value}</p>
      {sub && <p className="text-zinc-500 text-xs mt-1 truncate">{sub}</p>}
    </Card>
  );
}

export default function ModuleStripCard({ role = 'player' }) {
  const [data, setData] = useState({ streak: null, leaderboard: null, feed: null, sheets: null });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    const calls = [
      api.get('/streaks/me').catch(() => ({ data: null })),
      api.get('/leaderboard/effort').catch(() => ({ data: { rows: [] } })),
      api.get('/squad-feed?limit=5').catch(() => ({ data: [] })),
    ];
    if (role !== 'player') calls.push(api.get('/match-sheets').catch(() => ({ data: [] })));
    Promise.all(calls).then((res) => {
      if (!mounted) return;
      setData({
        streak: res[0]?.data,
        leaderboard: res[1]?.data,
        feed: res[2]?.data,
        sheets: res[3]?.data,
      });
    }).finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [role]);

  if (loading) {
    return <Card className="bg-zinc-900 border-zinc-800 p-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-zinc-500" /></Card>;
  }

  const topName = data.leaderboard?.rows?.[0]?.name || '—';
  const topScore = data.leaderboard?.rows?.[0]?.effort_score || 0;
  const feedCount = data.feed?.length || 0;
  const sheetsCount = data.sheets?.length || 0;
  const sheetsWins = data.sheets?.filter((s) => s.result === 'Win').length || 0;

  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="module-strip">
      <Tile
        icon={Flame}
        title="Training Streak"
        value={`${data.streak?.current_streak_days || 0}d`}
        sub={data.streak?.milestone_unlocked ? `${data.streak.milestone_unlocked}-day milestone unlocked` : data.streak?.next_milestone ? `${data.streak.next_milestone}-day next` : 'Build your streak'}
        testId="module-streak-tile"
        accent="#fb923c"
      />
      <Tile
        icon={Trophy}
        title="Effort Leaderboard"
        value={role === 'player' ? `#${data.leaderboard?.rows?.find(r => r.rank)?.rank || '—'}` : topScore.toString()}
        sub={role === 'player' ? '28-day window' : `Top: ${topName}`}
        testId="module-leaderboard-tile"
        accent="#facc15"
      />
      <Tile
        icon={MessageSquare}
        title="Squad Feed"
        value={feedCount}
        sub={feedCount > 0 ? `${data.feed?.[0]?.text?.slice(0, 40) || ''}…` : 'No posts yet'}
        testId="module-feed-tile"
        accent="#3b82f6"
      />
      {role === 'player' ? (
        <Tile
          icon={Users}
          title="My Squad"
          value="Active"
          sub="In-platform messaging only"
          testId="module-squad-tile"
          accent="#a855f7"
        />
      ) : (
        <Tile
          icon={ClipboardList}
          title="Match Sheets"
          value={sheetsCount}
          sub={sheetsCount > 0 ? `${sheetsWins} wins this season` : 'No matches recorded'}
          testId="module-sheets-tile"
          accent="#A3FF00"
        />
      )}
    </div>
  );
}
